'use client';
import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { FontIcon } from '@intigral/shared';
import PromoSlide, { IPromotion } from './Slide/PromoSlide';
import SliderThumbs from './Thumbs/SliderThumbs';
import { SlideSkeleton } from './SlideSkeleton';
import styles from './slider.module.sass';

interface IPromoSlider {
    promotions: IPromotion[];
    isRTL: boolean;
    /**
     * Trailer component with mute/unmute, player setup logic, logic for pausing playback when trailer is scrolled
     * out of view
     */
    Trailer: React.FC<any>;
    Player: any;
    /**
     * Interval of sliding to next slide in ms
     * @default 10000
     */
    nextSlideDelay?: number;
    /**
     * Timeout after which playback is started in ms
     * @default 3000
     */
    trailerDelay?: number;
    /** App config flag for enabling/desabling trailer */
    promoAutoTrailerEnable?: boolean;
    liveStreamAutoPlayEnable?: boolean;
    isLocked?: boolean;
    id?: string;
    isLoading?: boolean;
}

const PromoSlider = ({
    promotions,
    isRTL,
    Trailer,
    Player,
    nextSlideDelay = 10000,
    trailerDelay = 3000,
    promoAutoTrailerEnable = false,
    liveStreamAutoPlayEnable = false,
    id,
    isLoading,
}: IPromoSlider) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    // TODO: move into hook
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const nextSlide = useCallback(() => {
        if (!promotions?.length) return;
        setCurrentSlide((prevSlide) => (prevSlide + 1) % promotions?.length);
    }, [promotions?.length]);

    function prevSlide() {
        if (!promotions?.length) return;
        setCurrentSlide((prevSlide) => (prevSlide - 1 + promotions?.length) % promotions?.length);
    }

    function handleTouchStart(e: React.TouchEvent) {
        setTouchStart(e.touches[0].clientX);
    }

    function handleTouchMove(e: React.TouchEvent) {
        setTouchEnd(e.touches[0].clientX);
    }

    /**
     * Scroll to next/previous slide when user is swiping on mobile
     */
    function handleTouchEnd() {
        const dir = isRTL ? -1 : 1;

        if ((touchStart - touchEnd) * dir > 50) {
            nextSlide();
        }

        if ((touchStart - touchEnd) * dir < -50) {
            prevSlide();
        }
    }

    function handleCardClick(index: number) {
        setCurrentSlide(index);
    }

    const handleScrollDown = () => {
        const headerHeight = document.querySelector<HTMLElement>('.header')?.offsetHeight || 0;
        const scrollDistance = window.innerHeight - headerHeight * 2; // header height and compensation margin

        window.scrollBy({
            top: scrollDistance, // Scroll down by one screen height
            left: 0,
            behavior: 'smooth', // Smooth scroll
        });
    };

    useEffect(() => {
        setIsPaused(false);
    }, [currentSlide]);

    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            !isPaused && nextSlide();
        }, nextSlideDelay);

        return () => clearTimeout(timeoutRef.current);
    }, [isPaused, currentSlide, promotions?.length]);

    // visual feedback indicating that data is not yet loaded
    const areSlidesReady = promotions !== null && !isLoading;
    if (!areSlidesReady) {
        return <SlideSkeleton />;
    }

    const getActive = (currentSlide: number, index: number, live: string | undefined): boolean => {
        const isAutoPlayEnabled = !!(promoAutoTrailerEnable && (live ? liveStreamAutoPlayEnable : true));
        return currentSlide === index && isAutoPlayEnabled;
    };

    return (
        promotions &&
        promotions.length > 0 && (
            <div className={styles['slider-wrapper']} id={id} data-test-id={id}>
                <div className={`${styles['slider-container']} promotion-slider-container`}>
                    <div
                        className={styles.slider}
                        style={{ transform: `translateX(${currentSlide * 100 * (isRTL ? 1 : -1)}%)` }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        {promotions.map((p, index) => {
                            return (
                                <div key={`${index}`} className={styles.slide}>
                                    <PromoSlide
                                        promotion={p}
                                        Trailer={Trailer}
                                        setPauseSlide={setIsPaused}
                                        active={getActive(currentSlide, index, p.live)}
                                        goToNextSlide={nextSlide}
                                        Player={Player}
                                        trailerDelay={trailerDelay}
                                        priority={index === 0}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={styles.navigation} data-test-id="navigation-buttons">
                    <button
                        className={`${styles['arrow']} ${styles.prev}`}
                        onClick={prevSlide}
                        data-test-id="right-navigation-button"
                    >
                        <FontIcon name="left-arrow" size="smaller" md="small"></FontIcon>
                    </button>
                    <button
                        className={`${styles['arrow']} ${styles.next}`}
                        onClick={nextSlide}
                        data-test-id="left-navigation-button"
                    >
                        <FontIcon name="right-arrow" size="smaller" md="small"></FontIcon>
                    </button>
                </div>
                <SliderThumbs
                    currentSlide={currentSlide}
                    images={promotions.map((p) => p.images.smallThumbnail)}
                    handleCardClick={handleCardClick}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    live={promotions.map((p) => p.live || '')}
                />
                <div className={styles['scroll-down']} onClick={handleScrollDown}>
                    <FontIcon name="arrow-down" size="size60" md="size60" />
                </div>
            </div>
        )
    );
};

export { PromoSlider };
